import { breakpoints, Button, Heading, Inline, RadioGroup, Stack, Text, vars } from '@etg/wings';
import { CarrierLogo, ContentWrapper, ExpandableContent } from '@eti/components';
import { css, cx, etiColors, mediaQueries } from '@eti/styles';
import { CaretDown } from '@phosphor-icons/react';
import { useHeightContext, useProperty, useTranslation } from '@eti/providers';
import type { ChangeEvent } from 'react';
import { useCallback, useMemo, useRef, useState, useEffect } from 'react';
import type {
  BrandedFare as BrandedFareType,
  TravelerPrice,
  PaymentMethodPrice,
  TravelerNew,
} from '@eti/schema-types';
import { useMediaQuery } from 'usehooks-ts';
import { cancelForAnyReason, flexibleTicket } from '@eti/products';
import type { BoundType } from '../../../../order-details/models';
import TripDetailsTitle from '../TripDetailsTitle';
import {
  getToastMessage,
  getProductsIdFromSelectedFare,
  getCommonProductsInFareAndTrip,
  shouldShowInformationBox,
  modifyTripBrandedFareName,
  getTotalPrice,
} from '../utils';
import StickyFooter from '../../sticky-footer/StickyFooter';
import {
  MANUALLY_REMOVED_PRODUCTS,
  MANUALLY_ADDED_PRODUCTS,
} from '../../../../../constants/brandedFareConstants';
import PreSelectedProducts from './pre-selected-products/PreSelectedProducts';
import usePreSelectedExtraProducts from './hooks/usePreSelectedExtraProducts';
import BrandedFare from './BrandedFare';
import ToastWrapper from './toast/ToastWrapper';
import useBrandedFareOptions from './hooks/useBrandedFareOptions';
import type { AvailableExtraProductsType } from './model';
import useUpdateProductPriceBySelectedFare from './hooks/useUpdateProductPriceBySelectedFare';
import usePriceDisplayType from './hooks/usePriceDisplayType';
import { GFSPartner } from './constants';

const listingWrapperStyles = (isMediumScreenWidth: boolean, numberOfItems: number) => css`
  display: grid;
  grid-gap: 16px;
  grid-template-columns: 1fr;
  width: 100%;

  @media ${mediaQueries.medium.up} {
    grid-template-columns: ${numberOfItems < 3 || isMediumScreenWidth ? '1fr 1fr' : '1fr 1fr 1fr'};
  }
`;

const iconSizeStyles = css`
  height: 32px;
  width: 32px;
`;

const optionsLengthStyles = css`
  color: ${vars.colors.text.primary};
`;

const toggleButtonWrapperStyles = css`
  display: flex;
  margin-bottom: 18px;
  margin-top: 8px;
  position: relative;
`;

const toggleButtonStyles = css`
  left: 50%;
  position: absolute;
  transform: translateX(-50%);
`;

const iconStyles = (isShowingAllOptions: boolean) => css`
  transform: rotate(${isShowingAllOptions ? '180deg' : '0deg'});
`;

const contentWrapperStyles = (isContentToggleable: boolean) => css`
  margin-bottom: 45px;
  margin-top: 16px;
  ${isContentToggleable && `padding-bottom: 48px`};
`;

const headingStyles = css`
  color: ${etiColors.black};
`;

const headingWrapperStyles = (shouldDisplaySelectedTripCardV2: boolean) => css`
  justify-content: space-between;
  ${!shouldDisplaySelectedTripCardV2 && `margin-top: 20px;`};

  @media ${mediaQueries.medium.up} {
    align-items: center;
    ${!shouldDisplaySelectedTripCardV2 && `margin-top: 22px;`};
  }
`;

const cardsContainer = css`
  margin-top: 16px;
`;

const logoContainerStyles = css`
  gap: 4px;

  @media ${mediaQueries.small.only} {
    align-items: center;
    flex-direction: row;
    justify-content: space-between;
    margin-bottom: 8px;
  }
`;

const radioGroupStyles = css`
  position: relative;
`;

export const growContainerStyles = (headerAndStepIndicatorHeight: number) => css`
  display: flex;
  flex-direction: column;
  min-height: calc(100vh - ${headerAndStepIndicatorHeight}px);
`;

const stepIndicatorHeight = 45;

interface ToastMessage {
  description: string;
  id: number;
}

type Carrier = {
  code: string;
  name: string;
  logo: {
    small: string;
    large: string;
  };
};

interface BrandedFaresProps {
  availableExtraProducts: AvailableExtraProductsType[];
  brandedFares: BrandedFareType[];
  defaultSelectedFareKey: string;
  discountAmount: number;
  handleProceedButtonClick: (arg0: number[]) => void;
  partner: string;
  selectedBrandedFareSelectionKey: string;
  setSelectedBrandedFareSelectionKey: (arg0: string) => void;
  isSelectTripLoading: boolean;
  ticketingCarrierCodes: string[];
  travelerPrices: TravelerPrice[];
  isMediumScreenWidth: boolean;
  marketingCarrier: Carrier;
  paymentMethodPrices: PaymentMethodPrice[];
  recommendedFareKey: string;
  selectedTripPricePerAdult: number;
  bounds: BoundType[];
  tripTravelers: TravelerNew[];
}

const BrandedFares = ({
  availableExtraProducts,
  brandedFares = [],
  defaultSelectedFareKey,
  discountAmount,
  handleProceedButtonClick,
  partner,
  selectedBrandedFareSelectionKey,
  selectedTripPricePerAdult,
  setSelectedBrandedFareSelectionKey,
  paymentMethodPrices,
  isSelectTripLoading,
  ticketingCarrierCodes,
  travelerPrices,
  isMediumScreenWidth,
  marketingCarrier,
  recommendedFareKey,
  bounds,
  tripTravelers,
}: BrandedFaresProps) => {
  const { p } = useProperty();
  const { t } = useTranslation();

  const isWiderViewport = useMediaQuery(`(min-width: ${breakpoints._768})`);
  const { headerHeightContext } = useHeightContext();
  const { priceDisplayType } = usePriceDisplayType();

  const savedManuallyRemovedProductIds = localStorage.getItem(MANUALLY_REMOVED_PRODUCTS);
  const parsedManuallyRemovedProductIds =
    savedManuallyRemovedProductIds !== null ? JSON.parse(savedManuallyRemovedProductIds) : [];
  const [manuallyRemovedProductsId, setManuallyRemovedProductsId] = useState<number[]>(
    parsedManuallyRemovedProductIds,
  );

  const savedManuallyAddedProductIds = localStorage.getItem(MANUALLY_ADDED_PRODUCTS);
  const parsedManuallyAddedProductIds =
    savedManuallyAddedProductIds !== null ? JSON.parse(savedManuallyAddedProductIds) : [];
  const [manuallyAddedProductsId, setManuallyAddedProductsId] = useState<number[]>(
    parsedManuallyAddedProductIds,
  );

  const regexp = new RegExp(`^${GFSPartner}`);
  const isPartnerGoogleFlights = regexp.test(partner);
  const isPreselectedProductsEnabled = p(
    'YourSelectedTrip.Page.BrandedFares.V3.PreselectedProducts.Enabled',
  );

  const preSelectedExtraProducts = usePreSelectedExtraProducts(
    availableExtraProducts,
    travelerPrices?.length,
    isPartnerGoogleFlights,
  );

  const carriersWithSellableFeatures = p('BrandedFares.Sellable.Carriers.List');
  const productsInFare = getProductsIdFromSelectedFare(
    selectedBrandedFareSelectionKey || defaultSelectedFareKey,
    brandedFares,
    marketingCarrier,
    carriersWithSellableFeatures,
  );

  let commonProductsInFare = getCommonProductsInFareAndTrip(
    productsInFare as number[],
    preSelectedExtraProducts,
  );

  const initiallyRemovedProductIds = commonProductsInFare.filter(
    (productId) => !manuallyAddedProductsId.includes(productId),
  );
  const [removedProductsId, setRemovedProductsId] = useState<number[]>(initiallyRemovedProductIds);

  const [productIdsForToastAndInfo, setProductIdsForToastAndInfo] = useState<number[]>(
    initiallyRemovedProductIds,
  );
  const [toastMessages, setToastMessages] = useState<ToastMessage[]>([]);

  const toastMessageText = {
    multiProductRemoved: t('YourSelectedTrip.Preselected.MultiProduct.Removed.Toast.Text'),
    multiProductReAdded: t('YourSelectedTrip.Preselected.MultiProduct.ReAdded.Toast.Text'),
    oneOfAdded: t('YourSelectedTrip.Preselected.OneOf.ReAdded.Toast.Text'),
    oneOfRemoved: t('YourSelectedTrip.Preselected.OneOf.Removed.Toast.Text'),
    singleProductRemoved: t('YourSelectedTrip.Preselected.SingleProduct.Removed.Toast.Text'),
    singleProductReAdded: t('YourSelectedTrip.Preselected.SingleProduct.ReAdded.Toast.Text'),
  };

  //  "productIdsForToastAndInfo" this list is basically to store the ids of products for which we need to show toast message.
  //  in this list manually removed product ids which are also present in branded fares will not be present as we don't show toast  message for
  //  already mannually removed products.

  const addToastMessage = (messages: ToastMessage[]) => {
    const updatedToastMessages = [...toastMessages, ...messages];
    setToastMessages(updatedToastMessages);
  };

  const removeToastMessage = useCallback((id: number) => {
    setToastMessages((currentMessages) => {
      return currentMessages.filter((message) => message.id !== id);
    });
  }, []);

  const { alwaysVisibleOptions, handleToggle, isContentToggleable, isOpen, toggleableOptions } =
    useBrandedFareOptions(brandedFares, defaultSelectedFareKey, isMediumScreenWidth);

  const toggleableBrandedFareRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (toggleableBrandedFareRef.current && isOpen) {
      toggleableBrandedFareRef.current.focus();
    }
  }, [isOpen]);

  const shouldShowInformationBoxInFirstOption = shouldShowInformationBox(
    p('BrandedFares.HideInformationBox.Carrier'),
    ticketingCarrierCodes,
  );

  const modifiedBounds = defaultSelectedFareKey
    ? (modifyTripBrandedFareName(bounds, brandedFares, defaultSelectedFareKey) as BoundType[])
    : bounds;

  const handleSelectBrandedFare = (event: ChangeEvent<HTMLInputElement>) => {
    if (!isPreselectedProductsEnabled) {
      setSelectedBrandedFareSelectionKey(event.target.value);
      return;
    }

    const productIdsInFare = getProductsIdFromSelectedFare(
      event.target.value,
      brandedFares,
      marketingCarrier,
      carriersWithSellableFeatures,
    );

    commonProductsInFare = getCommonProductsInFareAndTrip(
      productIdsInFare as number[],
      preSelectedExtraProducts,
    );

    // Below line will remove the productIds from the `manuallyAddedProductsId` if the same is there in the `commonProductsInFare` to display deleted products information
    const filteredManuallyAddedProductsId = manuallyAddedProductsId.filter(
      (productId: number) => !commonProductsInFare.includes(productId),
    );
    setManuallyAddedProductsId(filteredManuallyAddedProductsId);
    localStorage.setItem(MANUALLY_ADDED_PRODUCTS, JSON.stringify(filteredManuallyAddedProductsId));

    // Below list "productsInFareNotManuallyRemoved" is to store the common ids which are there in the selected branded fare
    // as well as in preselected products, but not the ids which are manually added or removed from the preselected products section.
    const productsInFareNotManuallyRemoved = commonProductsInFare.filter((productId) => {
      return ![...manuallyRemovedProductsId].includes(productId);
    });
    const productIdsNotManuallyRemoved = [
      ...productsInFareNotManuallyRemoved.sort((a, b) => a - b),
    ];

    // Below list "preSelectedProductsNotManuallyRemoved" is to store the preselected product ids which
    // are not manually added or removed from the preselected products section.
    const preSelectedProductsNotManuallyRemoved = preSelectedExtraProducts
      .map(({ productId }) => {
        if (![...manuallyRemovedProductsId].includes(productId)) {
          return productId;
        }
        return null;
      })
      .filter(Boolean);
    const sortedProductIds = productIdsForToastAndInfo.sort((a, b) => a - b);
    const newToastMessages = getToastMessage(
      [...sortedProductIds],
      productIdsNotManuallyRemoved,
      preSelectedProductsNotManuallyRemoved.length,
      toastMessageText,
    );
    if (newToastMessages.length > 0) {
      addToastMessage(newToastMessages);
    }
    setProductIdsForToastAndInfo(productIdsNotManuallyRemoved);
    const updatedRemovedProductIds = removedProductsId.filter(
      (productId) => commonProductsInFare?.includes(productId),
    );

    setRemovedProductsId([
      ...new Set([
        ...commonProductsInFare,
        ...updatedRemovedProductIds,
        ...manuallyRemovedProductsId,
      ]),
    ]);
    setSelectedBrandedFareSelectionKey(event.target.value);
  };

  const productsHandler = (productId: number) => {
    let updatedRemovedProductList;
    let manuallyRemovedProductIds;
    let manuallyAddedProductIds;

    if (removedProductsId.includes(productId) || manuallyRemovedProductsId.includes(productId)) {
      updatedRemovedProductList = removedProductsId.filter((id) => id !== productId);
      manuallyRemovedProductIds = manuallyRemovedProductsId.filter((id) => id !== productId);
      manuallyAddedProductIds = [...new Set([...manuallyAddedProductsId, productId])];
      const updatedProductIdsForToastAndInfo = productIdsForToastAndInfo.filter(
        (id) => id !== productId,
      );
      setProductIdsForToastAndInfo(updatedProductIdsForToastAndInfo);
    } else {
      manuallyRemovedProductIds = [...new Set([...manuallyRemovedProductsId, productId])];
      manuallyAddedProductIds = manuallyAddedProductsId.filter((id) => id !== productId);
      updatedRemovedProductList = [...new Set([...removedProductsId, productId])];
    }

    setManuallyRemovedProductsId(manuallyRemovedProductIds);
    localStorage.setItem(MANUALLY_REMOVED_PRODUCTS, JSON.stringify(manuallyRemovedProductIds));
    setManuallyAddedProductsId(manuallyAddedProductIds);
    localStorage.setItem(MANUALLY_ADDED_PRODUCTS, JSON.stringify(manuallyAddedProductIds));
    setRemovedProductsId(updatedRemovedProductList);
  };

  const finalProductIdsToPurchase = useMemo(() => {
    let productIdsToPurchase = preSelectedExtraProducts.map(({ productId }) => productId);

    if (!isPreselectedProductsEnabled) {
      productIdsToPurchase = productIdsToPurchase.filter(
        (productId: number) =>
          !commonProductsInFare.includes(productId) ||
          productId === flexibleTicket.id ||
          productId === cancelForAnyReason.id,
      );
      return [...new Set([...productIdsToPurchase])];
    }

    productIdsToPurchase = productIdsToPurchase.filter(
      (productId: number) =>
        !manuallyRemovedProductsId.includes(productId) && !commonProductsInFare.includes(productId),
    );
    return [...new Set([...productIdsToPurchase, ...manuallyAddedProductsId])];
  }, [
    preSelectedExtraProducts,
    isPreselectedProductsEnabled,
    manuallyAddedProductsId,
    manuallyRemovedProductsId,
    commonProductsInFare,
  ]);

  const onClickHandler = () => {
    handleProceedButtonClick(finalProductIdsToPurchase);
  };

  const brandedFareDescription = t('BrandedFares.Text');
  const selectedFareKey = selectedBrandedFareSelectionKey || defaultSelectedFareKey;
  const priceUpdatedProducts = useUpdateProductPriceBySelectedFare(
    preSelectedExtraProducts,
    selectedFareKey,
    brandedFares,
    travelerPrices?.length,
  );

  const selectedBrandedFare = brandedFares.find(
    ({ selectionKey }: { selectionKey: string }) => selectionKey === selectedFareKey,
  );

  const brandedFareRef = useRef<HTMLDivElement | null>(null);
  const onClickHandle = () => {
    handleToggle();
    if (isOpen && brandedFareRef.current) {
      brandedFareRef.current.scrollIntoView({ behavior: 'auto' });
    }
  };

  const totalPrice = getTotalPrice(
    selectedBrandedFare!,
    priceDisplayType,
    finalProductIdsToPurchase,
    priceUpdatedProducts,
  );

  const headerAndStepIndicatorHeight = headerHeightContext + stepIndicatorHeight;
  const YSTPageVersion = p('YourSelectedTrip.Page.Version');
  const shouldDisplaySelectedTripCardV2 =
    (p('YourSelectedTrip.Page.SelectedTripCard.V3.Enabled') ||
      p('YourSelectedTrip.Page.BrandedFares.V3.SelectedTripCard.V3.Enabled')) &&
    YSTPageVersion === 2;

  return (
    <>
      <ContentWrapper
        className={cx(
          contentWrapperStyles(isContentToggleable),
          growContainerStyles(headerAndStepIndicatorHeight),
        )}
        data-testid="brandedFares"
      >
        {YSTPageVersion === 1 && <TripDetailsTitle bounds={modifiedBounds} />}
        {priceUpdatedProducts?.length > 0 &&
          !isPartnerGoogleFlights &&
          isPreselectedProductsEnabled && (
            <PreSelectedProducts
              commonProductsInFare={commonProductsInFare}
              manuallyAddedProductsId={manuallyAddedProductsId}
              manuallyRemovedProductsId={manuallyRemovedProductsId}
              preSelectedExtraProducts={priceUpdatedProducts}
              productsHandler={productsHandler}
            />
          )}
        <RadioGroup
          className={radioGroupStyles}
          name="brandedFares"
          onChange={handleSelectBrandedFare}
          selectedValue={selectedBrandedFareSelectionKey || defaultSelectedFareKey}
        >
          <Stack spacing={8}>
            <Inline
              className={headingWrapperStyles(shouldDisplaySelectedTripCardV2)}
              ref={brandedFareRef}
            >
              <Stack spacing={8}>
                <Heading
                  as="legend"
                  className={headingStyles}
                  data-testid="brandedFares-v3-header"
                  level={2}
                >
                  {t('BrandedFares.Header')}
                </Heading>
                {!isWiderViewport && (
                  <Text as="p" className={optionsLengthStyles}>
                    {t(
                      'BrandedFares.NumberOfTrips.Text',
                      isOpen ? brandedFares?.length : alwaysVisibleOptions?.length,
                      brandedFares?.length,
                    )}
                  </Text>
                )}
              </Stack>
              <Stack
                align="end"
                className={logoContainerStyles}
                data-testid="count-of-branded-fares-v3-visible"
                spacing={0}
              >
                <CarrierLogo
                  className={iconSizeStyles}
                  data-testid="tripDetails-carrier-logo"
                  id={marketingCarrier.code || ''}
                  imageAlt={marketingCarrier.name || ''}
                  key={marketingCarrier.code}
                  logo={marketingCarrier.logo}
                />
                {isWiderViewport && (
                  <Text as="p" className={optionsLengthStyles}>
                    {t(
                      'BrandedFares.NumberOfTrips.Text',
                      isOpen ? brandedFares?.length : alwaysVisibleOptions?.length,
                      brandedFares?.length,
                    )}
                  </Text>
                )}
              </Stack>
            </Inline>
            {brandedFareDescription && <p>{brandedFareDescription}</p>}
            <Stack className={cardsContainer}>
              <div
                className={listingWrapperStyles(isMediumScreenWidth, alwaysVisibleOptions.length)}
              >
                {alwaysVisibleOptions.map((brandedFareData, index) => (
                  <BrandedFare
                    brandedFareData={brandedFareData}
                    key={brandedFareData.id}
                    recommendedFareKey={recommendedFareKey}
                    selectedTripPricePerAdult={selectedTripPricePerAdult}
                    shouldShowInformationBox={index === 0 && shouldShowInformationBoxInFirstOption}
                    ticketingCarrierCodes={ticketingCarrierCodes}
                  />
                ))}
              </div>
              {toggleableOptions && (
                <ExpandableContent isExpanded={isOpen}>
                  <div
                    className={listingWrapperStyles(
                      isMediumScreenWidth,
                      alwaysVisibleOptions.length,
                    )}
                  >
                    {toggleableOptions.map((brandedFareData, index) => (
                      <BrandedFare
                        brandedFareData={brandedFareData}
                        {...(index === 0 && { ref: toggleableBrandedFareRef })}
                        key={brandedFareData.id}
                        recommendedFareKey={recommendedFareKey}
                        selectedTripPricePerAdult={selectedTripPricePerAdult}
                        ticketingCarrierCodes={ticketingCarrierCodes}
                      />
                    ))}
                  </div>
                </ExpandableContent>
              )}
            </Stack>
            {isContentToggleable && toggleableOptions && (
              <div className={toggleButtonWrapperStyles}>
                <Button
                  className={toggleButtonStyles}
                  data-testid="brandedFares-toggleOptions-button"
                  iconSuffix={<CaretDown className={iconStyles(isOpen)} />}
                  onClick={onClickHandle}
                  size="small"
                >
                  {isOpen
                    ? t('BrandedFares.NumberOfTrips.ShowLess.Label')
                    : t('BrandedFares.NumberOfTrips.ShowMore.Label', toggleableOptions.length)}
                </Button>
              </div>
            )}
          </Stack>
          {isPreselectedProductsEnabled && (
            <ToastWrapper deleteToast={removeToastMessage} toastList={toastMessages} />
          )}
        </RadioGroup>
      </ContentWrapper>
      <StickyFooter
        bounds={bounds}
        dataTestid="branded-fares-v3-sticky-footer"
        discountAmount={discountAmount}
        finalProductIdsToPurchase={finalProductIdsToPurchase}
        isSelectTripLoading={isSelectTripLoading}
        onClick={onClickHandler}
        paymentMethodPrices={paymentMethodPrices}
        selectedBrandedFare={selectedBrandedFare! as BrandedFareType}
        totalPrice={totalPrice}
        travelerPrices={travelerPrices}
        travelerPricesWithoutDiscounts={
          selectedBrandedFare?.travelerPricesWithoutPaymentDiscounts as TravelerPrice[]
        }
        tripTravelers={tripTravelers}
      />
    </>
  );
};

export default BrandedFares;
